/**
 * Fair Style Space v2 — playground states and chrome.
 *
 * Figma (desktop 1440x970): `Playground Empty` / `Playground Generating` frames —
 * six states, one shell. The shell never changes; the Canvas bottom toolbar, the chat
 * hero and the items sidebar read the state from here.
 */

import type { FairStyleSpaceCanvasSlotId } from './fair-style-space-canvas.data'
import silhouetteAccBelt from '@/assets/style-space/playground/slot-acc-belt.svg?url'
import silhouetteAccGlasses from '@/assets/style-space/playground/slot-acc-glasses.svg?url'
import silhouetteAccHat from '@/assets/style-space/playground/slot-acc-hat.svg?url'
import silhouetteBag from '@/assets/style-space/playground/slot-bag.svg?url'
import silhouetteBottom from '@/assets/style-space/playground/slot-bottom.svg?url'
import silhouetteOuterwear from '@/assets/style-space/playground/slot-outerwear.svg?url'
import silhouetteShoes from '@/assets/style-space/playground/slot-shoes.svg?url'
import silhouetteTop from '@/assets/style-space/playground/slot-top.svg?url'

/**
 * The silhouette the designer drew for each empty slot, exported from the `Look` frame.
 * The three accessory slots are, in the frame's own order: hat (1), glasses (2), belt (3).
 */
export const FAIR_SS2_PLAYGROUND_SLOT_SILHOUETTES: Record<FairStyleSpaceCanvasSlotId, string> = {
  outerwear: silhouetteOuterwear,
  top: silhouetteTop,
  bottom: silhouetteBottom,
  shoes: silhouetteShoes,
  bag: silhouetteBag,
  acc1: silhouetteAccHat,
  acc2: silhouetteAccGlasses,
  acc3: silhouetteAccBelt,
}

/**
 * Where each silhouette sits on the stage, in percentages of the `Look` frame
 * (704 x 604.84) — the vector's own rect, not its parent slot's.
 *
 * They differ: `Top`'s vector is 276 wide inside a 210 frame (it hangs out on both sides),
 * while the hat is 116x28 inside a 136x135 one, sitting high in it. Fitting every drawing
 * into its slot box shrinks the sweater and fills the empty bottom the designer left.
 */
export interface FairStyleSpaceV2PlaygroundRect {
  left: number
  top: number
  width: number
  height: number
}

export const FAIR_SS2_PLAYGROUND_SILHOUETTE_RECTS: Record<
  FairStyleSpaceCanvasSlotId,
  FairStyleSpaceV2PlaygroundRect
> = {
  outerwear: { left: 6.392, top: 8.576, width: 28.125, height: 43.317 },
  top: { left: 41.903, top: 13.891, width: 39.205, height: 24.8 },
  bottom: { left: 40.341, top: 52.132, width: 19.318, height: 39.845 },
  shoes: { left: 69.405, top: 81.042, width: 16.587, height: 6.508 },
  bag: { left: 76.42, top: 47.26, width: 14.773, height: 20.832 },
  acc1: { left: 4.775, top: 86.567, width: 16.587, height: 4.66 },
  acc2: { left: 81.96, top: 17.899, width: 10.795, height: 3.968 },
  acc3: { left: 15.199, top: 62.411, width: 19.318, height: 9.92 },
}

export function fairStyleSpaceV2PlaygroundRectStyle(
  rect: FairStyleSpaceV2PlaygroundRect,
): Record<string, string> {
  return {
    left: `${rect.left}%`,
    top: `${rect.top}%`,
    width: `${rect.width}%`,
    height: `${rect.height}%`,
  }
}

/** Slot → the CSS modifier carrying that slot's inset in the `Look` frame. */
export const FAIR_SS2_PLAYGROUND_SLOT_STYLE: Record<FairStyleSpaceCanvasSlotId, string> = {
  outerwear: 'outerwear',
  top: 'top',
  bottom: 'bottom',
  shoes: 'shoes',
  bag: 'bag',
  acc1: 'acc-a',
  acc2: 'acc-b',
  acc3: 'acc-c',
}

/** The six screens the designer drew, in the order a shopper walks them. */
export type FairStyleSpaceV2PlaygroundState =
  | 'empty'
  | 'one-item-picked'
  | 'look-complete'
  | 'chat-replace-flow'
  | 'generating-loading'
  | 'generating-results'

/**
 * The bar switches to work mode from the SECOND item, whatever its origin (designer's note on
 * `Playground - Work - Manual`): one item is still "launch", two are an outfit in the making.
 */
export const FAIR_SS2_PLAYGROUND_COMPLETE_MIN_SLOTS = 2

/** Looks the stylist deals into the results grid (two on top, one centered below). */
export const FAIR_SS2_PLAYGROUND_RESULT_CARDS = 3

export function resolveFairStyleSpaceV2PlaygroundState(input: {
  filledSlots: number
  selectedSlot: FairStyleSpaceCanvasSlotId | null
  hasConversation: boolean
  generating: boolean
  resultCount: number
}): FairStyleSpaceV2PlaygroundState {
  if (input.generating) return 'generating-loading'
  if (input.resultCount > 1) return 'generating-results'
  if (input.filledSlots === 0) return 'empty'
  if (input.filledSlots < FAIR_SS2_PLAYGROUND_COMPLETE_MIN_SLOTS) return 'one-item-picked'
  // the completed look with a piece picked and the thread open is the replace flow
  if (input.selectedSlot && input.hasConversation) return 'chat-replace-flow'
  return 'look-complete'
}

/* ——— items sidebar ——— */

export type FairStyleSpaceV2PlaygroundNavId = 'wardrobe' | 'capsule' | 'favourites' | 'picks'

export interface FairStyleSpaceV2PlaygroundNavDef {
  id: FairStyleSpaceV2PlaygroundNavId
  labelKey: string
}

/**
 * PageNav over the palette: WARDROBE / CAPSULE / FAVOURITES / PICKS (uppercase, 12/1.68).
 * Which tabs show is decided by the canvas (`.cursor/PLAYGROUND_V2_FLOW_SPEC.md` §5):
 * CAPSULE only when the canvas was entered from a capsule, PICKS after the first deal.
 */
export const FAIR_SS2_PLAYGROUND_NAV_DEFS: readonly FairStyleSpaceV2PlaygroundNavDef[] = [
  { id: 'wardrobe', labelKey: 'nav_wardrobe' },
  { id: 'capsule', labelKey: 'nav_capsule' },
  { id: 'favourites', labelKey: 'nav_favourites' },
  { id: 'picks', labelKey: 'nav_picks' },
]

/** T-096 / loader A — palette tiles drawn while the wardrobe loads: four rows of the 3-up grid. */
export const FAIR_SS2_PLAYGROUND_SKELETON_TILES = 12

export function resolveFairStyleSpaceV2PlaygroundNavTabs(input: {
  hasCapsule: boolean
  hasPicks: boolean
}): FairStyleSpaceV2PlaygroundNavDef[] {
  return FAIR_SS2_PLAYGROUND_NAV_DEFS.filter((def) => {
    if (def.id === 'capsule') return input.hasCapsule
    if (def.id === 'picks') return input.hasPicks
    return true
  })
}

/* ——— the source popover (Figma `Sourse popover (playground page) desktop`) ——— */

export type FairStyleSpaceV2PlaygroundSourceId = 'wardrobe' | 'mixed' | 'catalog'

export interface FairStyleSpaceV2PlaygroundSourceDef {
  id: FairStyleSpaceV2PlaygroundSourceId
  labelKey: string
}

export const FAIR_SS2_PLAYGROUND_SOURCE_DEFS: readonly FairStyleSpaceV2PlaygroundSourceDef[] = [
  { id: 'wardrobe', labelKey: 'source_wardrobe' },
  { id: 'mixed', labelKey: 'source_mixed' },
  { id: 'catalog', labelKey: 'source_catalog' },
]

export const FAIR_SS2_PLAYGROUND_DEFAULT_SOURCE: FairStyleSpaceV2PlaygroundSourceId = 'wardrobe'

/* ——— chat hero: the copy and the AI buttons differ per state ——— */

export interface FairStyleSpaceV2PlaygroundHero {
  titleKey: string
  subtitleKey: string | null
  /** Edit: one line without the mascot (entries table, entry 3). */
  mascot?: boolean
  /** AI buttons (`Surprise me`, `Complete the outfit`) — the gradient ones. */
  actions: readonly ('surprise' | 'dress-today' | 'complete')[]
}

export const FAIR_SS2_PLAYGROUND_HERO: Record<
  FairStyleSpaceV2PlaygroundState,
  FairStyleSpaceV2PlaygroundHero
> = {
  empty: {
    titleKey: 'hero_title_empty',
    subtitleKey: 'hero_subtitle_empty',
    actions: ['surprise'],
  },
  // one item on the canvas: "Complete the outfit" replaces "Surprise me" (Launch - 1 item)
  'one-item-picked': {
    titleKey: 'hero_title_empty',
    subtitleKey: 'hero_subtitle_picked',
    actions: ['complete'],
  },
  'look-complete': { titleKey: 'hero_title_empty', subtitleKey: null, actions: [] },
  'chat-replace-flow': { titleKey: 'hero_title_empty', subtitleKey: null, actions: [] },
  'generating-loading': {
    titleKey: 'hero_title_generating',
    subtitleKey: null,
    actions: [],
  },
  'generating-results': {
    titleKey: 'hero_title_results',
    subtitleKey: 'hero_subtitle_results',
    actions: [],
  },
}

/**
 * AIRA stays silent while the shopper assembles; with 2+ items and a pause ONE static chip
 * appears (`What's missing?`) — designer's note on `Work - Manual`.
 */
export const FAIR_SS2_PLAYGROUND_LOOK_CHIPS: readonly { id: string; labelKey: string }[] = [
  { id: 'whats-missing', labelKey: 'chip_whats_missing' },
]
export const FAIR_SS2_PLAYGROUND_LOOK_CHIP_DELAY_MS = 2000

/** A deal past this shows "Still working on it…" under the generating title. */
export const FAIR_SS2_PLAYGROUND_SLOW_DEAL_MS = 10_000

/* ——— the Canvas bottom toolbar ——— */

export interface FairStyleSpaceV2PlaygroundToolbar {
  /** The centered hint line above the controls; `null` once the shopper is working. */
  hintKey: string | null
  /** Empty state only: source button + occasion chips. */
  showSource: boolean
  showOccasions: boolean
  showClearAll: boolean
  /** Right-aligned actions, left→right as drawn. */
  actions: readonly ('complete' | 'shuffle' | 'save')[]
}

export const FAIR_SS2_PLAYGROUND_TOOLBAR: Record<
  FairStyleSpaceV2PlaygroundState,
  FairStyleSpaceV2PlaygroundToolbar | null
> = {
  empty: {
    hintKey: 'toolbar_hint_empty',
    showSource: true,
    showOccasions: true,
    showClearAll: false,
    actions: [],
  },
  // one item: the bar stays in launch mode (source + chips — a chip deals around the item)
  'one-item-picked': {
    hintKey: 'toolbar_hint_empty',
    showSource: true,
    showOccasions: true,
    showClearAll: false,
    actions: [],
  },
  'look-complete': {
    hintKey: null,
    showSource: false,
    showOccasions: false,
    showClearAll: true,
    actions: ['shuffle', 'save'],
  },
  'chat-replace-flow': {
    hintKey: null,
    showSource: false,
    showOccasions: false,
    showClearAll: true,
    actions: ['shuffle', 'save'],
  },
  // the generating frames have no toolbar at all — the canvas is the full 698
  'generating-loading': null,
  'generating-results': null,
}

/** The left sidebar is desaturated while the stylist deals (both generating frames). */
export function isFairStyleSpaceV2PlaygroundSidebarMuted(
  state: FairStyleSpaceV2PlaygroundState,
): boolean {
  return state === 'generating-loading' || state === 'generating-results'
}
